import { ArrowLeft, CircleAlert, CircleCheck, TriangleAlert } from 'lucide-react'
import { Link } from 'react-router-dom'
import { buildTime, seed } from '#event-config'
import { useEventConfig } from '@/hooks/useEvent'
import { Button } from '@/components/ui/primitives'
import { cn } from '@/lib/utils'
import { BrandMark } from './BrandMark'

/**
 * Mobile-first: one column, capped, with a sticky header the whole flow shares.
 *
 * `stickyBar` is rendered HERE rather than by the page, because a fixed bar and the
 * padding that keeps it from covering the footer have to agree. Left to the page,
 * they drifted: the pay button sat on top of the privacy and terms links, which are
 * the two links the SPEC requires to be reachable.
 */
export const PageShell = ({
  title,
  onBack,
  stickyBar,
  children,
}: {
  title: string
  onBack?: () => void
  stickyBar?: React.ReactNode
  children: React.ReactNode
}) => {
  /*
   * The live name, so renaming the event in /admin/settings reaches the buyer without
   * a deploy. `seed.title` covers the moments the config has not arrived yet and the
   * error state — the header must never render a blank where the event name goes.
   */
  const event = useEventConfig()
  const eventName = event.state === 'ready' ? event.data.title : seed.title

  /*
   * Home passes the event name AS its page title, so printing both lines there would
   * show the same words twice. Deduped here rather than by giving Home a second,
   * made-up heading: one screen's quirk should not cost every screen a prop.
   */
  const titleIsEventName = title === eventName

  return (
  <div className={cn('min-h-dvh bg-stone-50', stickyBar && 'pb-28')}>
    {/*
      * The brand colour, where the brand actually is.
      *
      * A white bar with black text is not neutral, it is absent — the buyer arrives
      * from a LINE message and has nothing telling them whose shop this is. The mark
      * and the event name in the event's own colour does that in the one strip that
      * stays on screen the whole way through.
      *
      * The event name is a link home on every screen: it is the one label that is the
      * same everywhere, so it is the one a buyer deep in the form will reach for to
      * get back out. Two lines fit inside the existing 56px bar, so the sticky header
      * does not grow.
      */}
    <header className="sticky top-0 z-30 bg-brand text-white shadow-sm">
      <div className="mx-auto flex min-h-14 max-w-md items-center gap-2 px-4">
        {onBack ? (
          <Button
            variant="ghost"
            size="icon"
            onClick={onBack}
            aria-label="ย้อนกลับ"
            className="-ml-2 shrink-0 text-white hover:bg-white/15"
          >
            <ArrowLeft className="size-5" aria-hidden />
          </Button>
        ) : null}

        {titleIsEventName ? (
          <Link to="/" className="flex min-w-0 items-center gap-2">
            <BrandMark className="size-6 shrink-0" />
            <h1 className="truncate text-base font-semibold">{title}</h1>
          </Link>
        ) : (
          <div className="min-w-0 flex-1">
            <Link
              to="/"
              className="flex w-fit max-w-full items-center gap-1.5 text-white/75 transition-colors hover:text-white"
            >
              <BrandMark className="size-3.5 shrink-0" />
              <span className="truncate text-xs">{eventName}</span>
            </Link>
            <h1 className="truncate text-base font-semibold leading-tight">{title}</h1>
          </div>
        )}
      </div>
    </header>

    <main className="mx-auto max-w-md px-4 py-4">{children}</main>

    <footer className="mx-auto max-w-md px-4 pb-8 pt-4 text-center text-xs text-stone-400">
      <a href={`${buildTime.mainSiteUrl}/privacy`} className="underline underline-offset-2">
        นโยบายความเป็นส่วนตัว
      </a>
      <span className="mx-2">·</span>
      <a href={`${buildTime.mainSiteUrl}/terms`} className="underline underline-offset-2">
        เงื่อนไขการใช้งาน
      </a>
      <p className="mt-2">
        ระบบเก็บเงินโดย{' '}
        <a href={buildTime.mainSiteUrl} className="font-medium underline underline-offset-2">
          จ่ายครบ
        </a>
      </p>
    </footer>

    {stickyBar ? (
      <div className="fixed inset-x-0 bottom-0 z-20 border-t border-stone-200 bg-white/95 px-4 pt-3 pb-safe backdrop-blur">
        <div className="mx-auto flex max-w-md items-center gap-3">{stickyBar}</div>
      </div>
    ) : null}
  </div>
  )
}

const TONE = {
  success: { icon: CircleCheck, box: 'bg-emerald-50 border-emerald-200', mark: 'text-emerald-600' },
  warning: { icon: TriangleAlert, box: 'bg-amber-50 border-amber-200', mark: 'text-amber-600' },
  error: { icon: CircleAlert, box: 'bg-red-50 border-red-200', mark: 'text-red-600' },
  info: { icon: CircleCheck, box: 'bg-sky-50 border-sky-200', mark: 'text-sky-600' },
} as const

/** One component for every dead end, so they all read the same way. */
export const StateMessage = ({
  tone,
  title,
  detail,
  action,
  children,
}: {
  tone: keyof typeof TONE
  title: string
  detail?: string
  action?: { label: string; onClick: () => void }
  children?: React.ReactNode
}) => {
  const { icon: Icon, box, mark } = TONE[tone]
  return (
    <div className={cn('flex flex-col items-center gap-3 rounded-lg border p-6 text-center', box)}>
      <Icon className={cn('size-8', mark)} aria-hidden />
      <h2 className="text-lg font-semibold">{title}</h2>
      {detail ? <p className="text-sm text-stone-600">{detail}</p> : null}
      {children}
      {action ? (
        <Button variant="outline" onClick={action.onClick}>
          {action.label}
        </Button>
      ) : null}
    </div>
  )
}
